import {createPortal} from 'react-dom';
import {useEffect, useRef, useState} from 'react';

import {copyStyles} from './copyStyles';

export const PopOutWindow: React.FC<{
    children: React.ReactNode;
    remotePopOut?: Window | null;
    title?: string;
    onUnload?: () => void;
}> = ({children, remotePopOut, title, onUnload}) => {
    const [container] = useState(() => document.createElement('div'));
    const popOutRef = useRef<Window | null>(null);

    // make sure we have a ref to a popOut
    useEffect(() => {
        if (remotePopOut) {
            popOutRef.current = remotePopOut;
        }
        if (!popOutRef.current) {
            popOutRef.current = window.open('', '');
        }
        if (popOutRef.current) {
            popOutRef.current.document.title = title ?? 'Pexip One';
        }
    }, [remotePopOut, title]);

    useEffect(() => {
        const popOutCopy = popOutRef.current;

        const releasePopOut = () => {
            if (!popOutCopy) {
                return;
            }
            popOutCopy.removeEventListener('beforeunload', releasePopOut);
            window.removeEventListener('beforeunload', releasePopOut);
            if (!popOutCopy?.closed) {
                popOutCopy.close();
            }
            if (typeof onUnload === 'function') {
                onUnload();
            }
        };
        if (popOutCopy) {
            popOutCopy.document.body.appendChild(container);
            copyStyles(document, popOutCopy);
            popOutCopy.addEventListener('beforeunload', releasePopOut);
            window.addEventListener('beforeunload', releasePopOut);
        }

        return () => releasePopOut();
    }, [container, onUnload]);

    if (!container) {
        return null;
    }
    return createPortal(children, container);
};
